// Каталог сущностей и полей — словарь, которым ограничены представления.
//
// Зачем он вообще нужен. Представление собирается по описанию, которое приходит
// снаружи (в конечном счёте — от модели), а снаружи приходит текст. Если этот
// текст попадёт в запрос как есть, мы разом теряем и изоляцию пространств, и
// права. Каталог переворачивает задачу: запрос собирается ИЗ каталога по ключу,
// а всё, чего в каталоге нет, отклоняется на валидации. Невыразимое здесь —
// нельзя попросить.
//
// Второе назначение — заменять выдумку отказом. У документа нет «даты
// заключения»: есть `status: signed`, `createdAt` и `updatedAt`. Без каталога
// модель придумает `signedAt`, и пользователь получит правдоподобно неверную
// таблицу; с каталогом она обязана сказать, что такого поля мы не храним.
//
// Файл намеренно без единого импорта из Prisma: его читает и клиент (подписи,
// отрисовка), а тянуть в браузерный бандл `@prisma/client` нельзя. Связь со
// схемой держит тест `catalog.test.ts` — он сверяет каждое поле с
// `prisma/schema.prisma`.

/** Тип поля определяет и отрисовку, и набор допустимых операций фильтра. */
export type FieldType =
  | 'string'
  | 'text'
  | 'number'
  | 'money'
  | 'date'
  | 'boolean'
  | 'enum'
  | 'tags'
  | 'ref'
  /**
   * Все привязки записи одной ячейкой. Нужен там, где связей у записи много и
   * колонкой «привязана к сделке» их не выразить: у заметки привязок может быть
   * сразу несколько (контакт, его компания и сделка). Читается из связи-таблицы,
   * перечисленной полями с `ref.through`.
   */
  | 'links';

export interface EnumOption {
  value: string;
  label: string;
}

export interface CatalogField {
  /** Имя поля в Prisma-модели; для `ref` — имя связи. */
  key: string;
  label: string;
  type: FieldType;
  /** Значения и их подписи для `type: 'enum'`. */
  options?: readonly EnumOption[];
  /**
   * Есть ли индекс, покрывающий фильтр по этому полю.
   *
   * Индексы у нас вида `(workspaceId, …)`; фильтр по неиндексированному полю
   * даёт последовательный скан. Пометка нужна, чтобы агент предпочитал дешёвые
   * фильтры, а не чтобы запрещать дорогие.
   */
  indexed?: boolean;
  /** Поле нельзя использовать в фильтре (тяжёлые тексты, вычисляемые связи). */
  notFilterable?: boolean;
  /** Поле нельзя использовать в сортировке. */
  notSortable?: boolean;
  /**
   * Поле годится только для фильтра — ни колонкой, ни разрезом, ни сортировкой.
   * Так помечены связи «многие ко многим»: у заметки их может быть несколько,
   * и единственного значения для ячейки просто не существует.
   */
  filterOnly?: boolean;
  /** Только для `type: 'ref'`. */
  ref?: CatalogRef;
}

export interface CatalogRef {
  /** Сущность каталога, на которую указывает связь; null — вне каталога (User, WorkspaceMember). */
  entity: EntityKey | null;
  /**
   * Имя делегата Prisma для целей вне каталога — по нему разрешаются подписи в
   * разрезах («сумма по ответственным»). Для целей внутри каталога модель
   * берётся из самой сущности.
   */
  model?: string;
  /**
   * Есть ли у цели колонка `workspaceId`. У `User` её нет: пользователь живёт
   * вне пространств. Выборка подписей по идентификаторам, пришедшим из записей
   * нашего пространства, доступа не расширяет — но умолчание здесь опасное,
   * поэтому исключение записано явно, а не выведено по месту.
   */
  tenantScoped?: boolean;
  /**
   * Пути до полей подписи внутри связанной записи; склеиваются пробелом.
   * Массив нужен из-за контакта: имя человека — это `firstName` + `lastName`.
   */
  labelPath: readonly string[];
  /** Внешний ключ на самой записи — по нему фильтруют и по нему же строят ссылку. */
  foreignKey: string;
  /**
   * Имя связи-посредника, если привязка живёт в отдельной таблице, а не
   * колонкой на записи. У заметки каноническая привязка — `note_links`:
   * одна заметка может висеть сразу на сделке и на компании, поэтому колонкой
   * её не выразить. Тогда `foreignKey` — это колонка таблицы связей.
   */
  through?: string;
  /**
   * Пути, которыми запись наследует эту привязку, когда своей колонки нет.
   * Порядок значим: побеждает первый непустой. У записи всегда ровно ОДНА
   * эффективная привязка — иначе задача, чей контакт ведёт к фирме A, а сделка
   * к фирме B, попала бы в выдачу обеих, и сумма по разрезу превысила бы число
   * задач.
   *
   * Путь — это «эффективная привязка соседа», посчитанная тем же правилом
   * рекурсивно. Поэтому «задача → сделка → компания» автоматически учитывает,
   * что компания сделки сама может быть от её контакта: правило сделки живёт в
   * одном месте, и разъехаться они не могут.
   *
   * Соответствующее поле у соседа ищется по цели: то, что указывает на ту же
   * сущность каталога. У контакта это `companyRef`, у сделки — `company`.
   */
  inheritedVia?: readonly InheritedPath[];
}

export interface InheritedPath {
  /** Имя связи на записи: `contact`, `deal`, `document`, `task`, `event`. */
  relation: string;
  /**
   * Сущность каталога на том конце связи. Имя связи её не задаёт: у активности
   * связь на встречу называется `event`, а колонка — `calendarEventId`.
   */
  entity: EntityKey;
  /**
   * Колонка внешнего ключа на самой записи. Нужна именно колонка: связи заданы
   * составным ключом `[workspaceId, id]`, и Prisma на `{ deal: null }` возвращает
   * пусто даже там, где `dealId` пуст. Проверка «соседа нет» идёт по колонке.
   */
  foreignKey: string;
  /** Есть ли у посредника мягкое удаление: удалённый привязку не передаёт. */
  softDelete?: boolean;
}

export interface CatalogEntity {
  key: string;
  label: string;
  labelPlural: string;
  /** Имя делегата Prisma (`prisma[model]`). */
  model: string;
  /** Право на чтение; проверяется `hasPermission` из tenant-core. */
  permission: string;
  /** Есть ли мягкое удаление. У журнала событий его нет — записи не правятся. */
  softDelete: boolean;
  /** Маршрут карточки; отсутствует у того, у чего карточки нет. */
  route?: string;
  defaultSort: { field: string; direction: 'asc' | 'desc' };
  defaultColumns: readonly string[];
  fields: readonly CatalogField[];
}

const TIMESTAMPS: readonly CatalogField[] = [
  { key: 'createdAt', label: 'Создано', type: 'date', indexed: false },
  { key: 'updatedAt', label: 'Изменено', type: 'date', indexed: true },
];

const DEAL_STAGE: readonly EnumOption[] = [
  { value: 'New', label: 'Новая' },
  { value: 'Contacted', label: 'Связались' },
  { value: 'Negotiation', label: 'Переговоры' },
  { value: 'Won', label: 'Выиграна' },
  { value: 'Lost', label: 'Проиграна' },
];

const DOCUMENT_TYPE: readonly EnumOption[] = [
  { value: 'contract', label: 'Договор' },
  { value: 'invoice', label: 'Счёт' },
  { value: 'act', label: 'Акт' },
];

const DOCUMENT_STATUS: readonly EnumOption[] = [
  { value: 'draft', label: 'Черновик' },
  { value: 'sent', label: 'Отправлен' },
  { value: 'signed', label: 'Подписан' },
  { value: 'cancelled', label: 'Отменён' },
];

const TASK_STATUS: readonly EnumOption[] = [
  { value: 'todo', label: 'К выполнению' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'blocked', label: 'Заблокирована' },
  { value: 'done', label: 'Выполнена' },
  { value: 'cancelled', label: 'Отменена' },
];

const TASK_PRIORITY: readonly EnumOption[] = [
  { value: 'low', label: 'Низкий' },
  { value: 'medium', label: 'Средний' },
  { value: 'high', label: 'Высокий' },
  { value: 'urgent', label: 'Срочный' },
];

const MEETING_STATUS: readonly EnumOption[] = [
  { value: 'scheduled', label: 'Запланирована' },
  { value: 'completed', label: 'Состоялась' },
  { value: 'cancelled', label: 'Отменена' },
];

const MEMORY_LEVEL: readonly EnumOption[] = [
  { value: 'l1', label: 'L1 — факты' },
  { value: 'l2', label: 'L2 — сводки' },
  { value: 'l3', label: 'L3 — смысловая' },
];

/**
 * Ссылка на участника пространства: подпись берётся из связанного пользователя.
 * `User.name` в схеме обязательно, поэтому запасного пути не нужно.
 */
function memberRef(key: string, label: string, foreignKey: string): CatalogField {
  return {
    key,
    label,
    type: 'ref',
    ref: {
      entity: null,
      model: 'workspaceMember',
      tenantScoped: true,
      labelPath: ['user.name'],
      foreignKey,
    },
    indexed: true,
  };
}

/**
 * Привязка заметки через `note_links` — только для фильтра.
 *
 * Заметка может висеть сразу на нескольких записях, поэтому «его заметки»
 * выражается вопросом «есть ли среди привязок вот эта», а не колонкой со
 * значением. Колонка `(workspaceId, <ключ>)` в таблице связей проиндексирована,
 * так что фильтр дешёвый.
 *
 * Наследование у такой привязки работает по правилу «хоть одна привязка ведёт к
 * X», без приоритета путей. У записи с одной колонкой эффективная привязка ровно
 * одна, потому что колонка одна; у заметки привязок несколько СРАЗУ, и обе
 * поставил человек руками — выбирать между ними приоритетом значило бы спрятать
 * половину правды. Аргумент, которым приоритет обоснован для колонок («сумма по
 * разрезу превысит общее число записей»), здесь не работает: `filterOnly` не
 * пускает поле ни в колонку, ни в разрез, так что суммировать нечего.
 *
 * `relation` у пути — имя связи на СТРОКЕ СВЯЗЕЙ (`note_links.calendarEvent`), а
 * не на заметке; `foreignKey` — колонка той же строки.
 */
function linkedRef(
  key: string,
  label: string,
  entity: EntityKey,
  foreignKey: string,
  inheritedVia: readonly InheritedPath[] = [],
): CatalogField {
  return {
    key,
    label,
    type: 'ref',
    filterOnly: true,
    indexed: true,
    ref: {
      entity,
      labelPath: [],
      foreignKey,
      through: 'links',
      ...(inheritedVia.length === 0 ? {} : { inheritedVia }),
    },
  };
}

/**
 * Пути от строки связей заметки к соседу. Порядок здесь не приоритет (у заметки
 * приоритета нет), а просто перечисление — но он же задаёт порядок ветвей `OR`,
 * поэтому дешёвые связи идут первыми.
 */
const NOTE_LINK_VIA_CONTACT: InheritedPath = {
  relation: 'contact',
  entity: 'contact',
  foreignKey: 'contactId',
  softDelete: true,
};
const NOTE_LINK_VIA_DEAL: InheritedPath = {
  relation: 'deal',
  entity: 'deal',
  foreignKey: 'dealId',
  softDelete: true,
};
const NOTE_LINK_VIA_TASK: InheritedPath = {
  relation: 'task',
  entity: 'task',
  foreignKey: 'taskId',
  softDelete: true,
};
const NOTE_LINK_VIA_DOCUMENT: InheritedPath = {
  relation: 'document',
  entity: 'document',
  foreignKey: 'documentId',
  softDelete: true,
};
// Связь на встречу называется `calendarEvent`, а колонка `calendarEventId` —
// вывести одно из другого нельзя, поэтому оба записаны явно.
const NOTE_LINK_VIA_MEETING: InheritedPath = {
  relation: 'calendarEvent',
  entity: 'meeting',
  foreignKey: 'calendarEventId',
  softDelete: true,
};

const DEAL: CatalogEntity = {
  key: 'deal',
  label: 'Сделка',
  labelPlural: 'Сделки',
  model: 'deal',
  permission: 'crm:deals:read',
  softDelete: true,
  route: '/deals',
  defaultSort: { field: 'updatedAt', direction: 'desc' },
  defaultColumns: ['title', 'amount', 'stage', 'company', 'updatedAt'],
  fields: [
    { key: 'title', label: 'Название', type: 'string' },
    { key: 'amount', label: 'Сумма', type: 'money' },
    { key: 'currency', label: 'Валюта', type: 'string' },
    { key: 'stage', label: 'Стадия', type: 'enum', options: DEAL_STAGE, indexed: true },
    { key: 'source', label: 'Источник', type: 'string' },
    { key: 'nextActivityDue', label: 'Следующий шаг', type: 'date', indexed: true },
    { key: 'lastActivityAt', label: 'Последняя активность', type: 'date' },
    { key: 'tags', label: 'Метки', type: 'tags' },
    {
      key: 'company',
      label: 'Компания',
      type: 'ref',
      ref: {
        entity: 'company',
        labelPath: ['name'],
        foreignKey: 'companyId',
        // Компания сделки часто стоит только у контакта — такие сделки тоже
        // принадлежат компании.
        inheritedVia: [
          { relation: 'contact', entity: 'contact', foreignKey: 'contactId', softDelete: true },
        ],
      },
      indexed: true,
    },
    {
      key: 'contact',
      label: 'Контакт',
      type: 'ref',
      ref: { entity: 'contact', labelPath: ['firstName', 'lastName'], foreignKey: 'contactId' },
      indexed: true,
    },
    {
      key: 'owner',
      label: 'Ответственный',
      type: 'ref',
      ref: {
        entity: null,
        model: 'user',
        tenantScoped: false,
        labelPath: ['name'],
        foreignKey: 'ownerId',
      },
      indexed: true,
    },
    ...TIMESTAMPS,
  ],
};

const DOCUMENT: CatalogEntity = {
  key: 'document',
  label: 'Документ',
  labelPlural: 'Документы',
  model: 'document',
  permission: 'crm:documents:read',
  softDelete: true,
  route: '/documents',
  defaultSort: { field: 'createdAt', direction: 'desc' },
  defaultColumns: ['title', 'type', 'status', 'amount', 'company', 'createdAt'],
  fields: [
    { key: 'title', label: 'Название', type: 'string' },
    { key: 'type', label: 'Вид', type: 'enum', options: DOCUMENT_TYPE, indexed: true },
    { key: 'status', label: 'Статус', type: 'enum', options: DOCUMENT_STATUS, indexed: true },
    { key: 'amount', label: 'Сумма', type: 'money' },
    { key: 'currency', label: 'Валюта', type: 'string' },
    // Тело договора — длинный текст. Показывать в колонке бессмысленно,
    // сортировать тем более; поиск по вхождению оставлен намеренно.
    { key: 'body', label: 'Текст', type: 'text', notSortable: true },
    {
      key: 'company',
      label: 'Компания',
      type: 'ref',
      ref: {
        entity: 'company',
        labelPath: ['name'],
        foreignKey: 'companyId',
        inheritedVia: [
          { relation: 'contact', entity: 'contact', foreignKey: 'contactId', softDelete: true },
          { relation: 'deal', entity: 'deal', foreignKey: 'dealId', softDelete: true },
        ],
      },
      indexed: true,
    },
    {
      key: 'contact',
      label: 'Контакт',
      type: 'ref',
      ref: {
        entity: 'contact',
        labelPath: ['firstName', 'lastName'],
        foreignKey: 'contactId',
        inheritedVia: [
          { relation: 'deal', entity: 'deal', foreignKey: 'dealId', softDelete: true },
        ],
      },
      indexed: true,
    },
    {
      key: 'deal',
      label: 'Сделка',
      type: 'ref',
      ref: { entity: 'deal', labelPath: ['title'], foreignKey: 'dealId' },
      indexed: true,
    },
    // «Даты заключения» у договора нет, и заводить её мы не стали. Подпись
    // здесь намеренно длиннее общей: «Создано» рядом с договором читается как
    // дата подписания, а это дата появления записи в системе — разные вещи.
    // Когда просят несуществующее, подставлять молча нельзя: агент обязан
    // спросить, что из имеющегося подойдёт.
    { key: 'createdAt', label: 'Дата создания записи', type: 'date' },
    { key: 'updatedAt', label: 'Изменено', type: 'date', indexed: true },
  ],
};

const COMPANY: CatalogEntity = {
  key: 'company',
  label: 'Компания',
  labelPlural: 'Компании',
  model: 'company',
  permission: 'crm:companies:read',
  softDelete: true,
  route: '/companies',
  defaultSort: { field: 'name', direction: 'asc' },
  defaultColumns: ['name', 'industry', 'domain', 'phone', 'updatedAt'],
  fields: [
    { key: 'name', label: 'Название', type: 'string', indexed: true },
    { key: 'domain', label: 'Домен', type: 'string' },
    { key: 'industry', label: 'Отрасль', type: 'string' },
    { key: 'website', label: 'Сайт', type: 'string' },
    { key: 'phone', label: 'Телефон', type: 'string' },
    { key: 'description', label: 'Описание', type: 'text', notSortable: true },
    { key: 'tags', label: 'Метки', type: 'tags' },
    memberRef('ownerMember', 'Ответственный', 'ownerMemberId'),
    ...TIMESTAMPS,
  ],
};

const CONTACT: CatalogEntity = {
  key: 'contact',
  label: 'Контакт',
  labelPlural: 'Контакты',
  model: 'contact',
  permission: 'crm:contacts:read',
  softDelete: true,
  route: '/contacts',
  defaultSort: { field: 'lastName', direction: 'asc' },
  defaultColumns: ['firstName', 'lastName', 'email', 'phone', 'companyRef'],
  fields: [
    { key: 'firstName', label: 'Имя', type: 'string' },
    { key: 'lastName', label: 'Фамилия', type: 'string', indexed: true },
    { key: 'email', label: 'Почта', type: 'string', indexed: true },
    { key: 'phone', label: 'Телефон', type: 'string', indexed: true },
    { key: 'role', label: 'Должность', type: 'string' },
    { key: 'source', label: 'Источник', type: 'string' },
    // У контакта ДВА «компании»: строковая legacy-колонка `company` и настоящая
    // связь `companyRef`. Обе оставлены с разными подписями — иначе выбор между
    // ними становится угадыванием.
    { key: 'company', label: 'Компания (текстом)', type: 'string' },
    {
      key: 'companyRef',
      label: 'Компания',
      type: 'ref',
      ref: { entity: 'company', labelPath: ['name'], foreignKey: 'companyId' },
      indexed: true,
    },
    { key: 'tags', label: 'Метки', type: 'tags' },
    { key: 'notes', label: 'Заметки текстом', type: 'text', notSortable: true },
    ...TIMESTAMPS,
  ],
};

const TASK: CatalogEntity = {
  key: 'task',
  label: 'Задача',
  labelPlural: 'Задачи',
  model: 'task',
  permission: 'crm:tasks:read',
  softDelete: true,
  route: '/tasks',
  defaultSort: { field: 'dueAt', direction: 'asc' },
  defaultColumns: ['title', 'status', 'priority', 'dueAt', 'assigneeMember'],
  fields: [
    { key: 'title', label: 'Название', type: 'string' },
    { key: 'description', label: 'Описание', type: 'text', notSortable: true },
    { key: 'status', label: 'Статус', type: 'enum', options: TASK_STATUS, indexed: true },
    { key: 'priority', label: 'Приоритет', type: 'enum', options: TASK_PRIORITY },
    { key: 'dueAt', label: 'Срок', type: 'date', indexed: true },
    { key: 'completedAt', label: 'Выполнена', type: 'date' },
    memberRef('assigneeMember', 'Исполнитель', 'assigneeMemberId'),
    {
      key: 'deal',
      label: 'Сделка',
      type: 'ref',
      ref: {
        entity: 'deal',
        labelPath: ['title'],
        foreignKey: 'dealId',
        inheritedVia: [
          { relation: 'document', entity: 'document', foreignKey: 'documentId', softDelete: true },
        ],
      },
      indexed: true,
    },
    {
      key: 'company',
      label: 'Компания',
      type: 'ref',
      ref: {
        entity: 'company',
        labelPath: ['name'],
        foreignKey: 'companyId',
        inheritedVia: [
          { relation: 'contact', entity: 'contact', foreignKey: 'contactId', softDelete: true },
          { relation: 'deal', entity: 'deal', foreignKey: 'dealId', softDelete: true },
          { relation: 'document', entity: 'document', foreignKey: 'documentId', softDelete: true },
        ],
      },
      indexed: true,
    },
    {
      key: 'contact',
      label: 'Контакт',
      type: 'ref',
      ref: {
        entity: 'contact',
        labelPath: ['firstName', 'lastName'],
        foreignKey: 'contactId',
        inheritedVia: [
          { relation: 'deal', entity: 'deal', foreignKey: 'dealId', softDelete: true },
          { relation: 'document', entity: 'document', foreignKey: 'documentId', softDelete: true },
        ],
      },
      indexed: true,
    },
    {
      key: 'document',
      label: 'Документ',
      type: 'ref',
      ref: { entity: 'document', labelPath: ['title'], foreignKey: 'documentId' },
      indexed: true,
    },
    ...TIMESTAMPS,
  ],
};

const NOTE: CatalogEntity = {
  key: 'note',
  label: 'Заметка',
  labelPlural: 'Заметки',
  model: 'note',
  permission: 'crm:notes:read',
  softDelete: true,
  route: '/notes',
  defaultSort: { field: 'createdAt', direction: 'desc' },
  defaultColumns: ['body', 'linkedTo', 'createdByMember', 'createdAt'],
  fields: [
    { key: 'body', label: 'Текст', type: 'text', notSortable: true },
    memberRef('createdByMember', 'Автор', 'createdByMemberId'),
    // Привязки — через `note_links` и только фильтром: показать привязку одной
    // ячейкой нельзя, их у заметки может быть несколько.
    //
    // Раньше рядом лежали legacy-колонки одиночной привязки под этими же
    // естественными именами (`deal` — колонка `dealId` на записи), а привязки
    // через links назывались `linkedDeal` и т.д. Модель закономерно выбирала
    // интуитивное имя — и фильтровала по legacy-колонке, которая при
    // канонической привязке пуста: «заметки этой сделки» возвращали ничего.
    // Из словаря legacy-колонки убраны; в базе они остаются (конвенция
    // «legacy не удаляем»), но спросить их через представление больше нельзя.
    //
    // Наследование: заметка на задаче фирмы — это заметка по фирме. Без путей
    // «заметки по фирме» показывали только те, что привязаны к фирме напрямую,
    // хотя человек привязывал их к её задаче или договору.
    linkedRef('deal', 'Привязана к сделке', 'deal', 'dealId', [
      NOTE_LINK_VIA_TASK,
      NOTE_LINK_VIA_DOCUMENT,
      NOTE_LINK_VIA_MEETING,
    ]),
    linkedRef('company', 'Привязана к компании', 'company', 'companyId', [
      NOTE_LINK_VIA_CONTACT,
      NOTE_LINK_VIA_DEAL,
      NOTE_LINK_VIA_TASK,
      NOTE_LINK_VIA_DOCUMENT,
      NOTE_LINK_VIA_MEETING,
    ]),
    linkedRef('contact', 'Привязана к контакту', 'contact', 'contactId', [
      NOTE_LINK_VIA_DEAL,
      NOTE_LINK_VIA_TASK,
      NOTE_LINK_VIA_DOCUMENT,
      NOTE_LINK_VIA_MEETING,
    ]),
    // Задача, встреча и документ — сами цели: наследовать их не от кого.
    linkedRef('task', 'Привязана к задаче', 'task', 'taskId'),
    linkedRef('meeting', 'Привязана к встрече', 'meeting', 'calendarEventId'),
    linkedRef('document', 'Привязана к документу', 'document', 'documentId'),
    ...TIMESTAMPS,
  ],
};

const MEETING: CatalogEntity = {
  key: 'meeting',
  label: 'Встреча',
  labelPlural: 'Встречи',
  model: 'calendarEvent',
  permission: 'crm:meetings:read',
  softDelete: true,
  route: '/meetings',
  defaultSort: { field: 'startsAt', direction: 'desc' },
  defaultColumns: ['title', 'startsAt', 'endsAt', 'status', 'location'],
  fields: [
    { key: 'title', label: 'Название', type: 'string' },
    { key: 'description', label: 'Описание', type: 'text', notSortable: true },
    { key: 'startsAt', label: 'Начало', type: 'date', indexed: true },
    { key: 'endsAt', label: 'Окончание', type: 'date' },
    { key: 'timezone', label: 'Часовой пояс', type: 'string' },
    { key: 'location', label: 'Место', type: 'string' },
    { key: 'status', label: 'Статус', type: 'enum', options: MEETING_STATUS, indexed: true },
    memberRef('ownerMember', 'Ответственный', 'ownerMemberId'),
    {
      key: 'deal',
      label: 'Сделка',
      type: 'ref',
      ref: {
        entity: 'deal',
        labelPath: ['title'],
        foreignKey: 'dealId',
        inheritedVia: [
          { relation: 'document', entity: 'document', foreignKey: 'documentId', softDelete: true },
          { relation: 'task', entity: 'task', foreignKey: 'taskId', softDelete: true },
        ],
      },
      indexed: true,
    },
    {
      key: 'company',
      label: 'Компания',
      type: 'ref',
      ref: {
        entity: 'company',
        labelPath: ['name'],
        foreignKey: 'companyId',
        inheritedVia: [
          { relation: 'contact', entity: 'contact', foreignKey: 'contactId', softDelete: true },
          { relation: 'deal', entity: 'deal', foreignKey: 'dealId', softDelete: true },
          { relation: 'document', entity: 'document', foreignKey: 'documentId', softDelete: true },
          { relation: 'task', entity: 'task', foreignKey: 'taskId', softDelete: true },
        ],
      },
      indexed: true,
    },
    {
      key: 'contact',
      label: 'Контакт',
      type: 'ref',
      ref: {
        entity: 'contact',
        labelPath: ['firstName', 'lastName'],
        foreignKey: 'contactId',
        inheritedVia: [
          { relation: 'deal', entity: 'deal', foreignKey: 'dealId', softDelete: true },
          { relation: 'document', entity: 'document', foreignKey: 'documentId', softDelete: true },
          { relation: 'task', entity: 'task', foreignKey: 'taskId', softDelete: true },
        ],
      },
      indexed: true,
    },
    {
      key: 'task',
      label: 'Задача',
      type: 'ref',
      ref: { entity: 'task', labelPath: ['title'], foreignKey: 'taskId' },
      indexed: true,
    },
    {
      key: 'document',
      label: 'Документ',
      type: 'ref',
      ref: { entity: 'document', labelPath: ['title'], foreignKey: 'documentId' },
      indexed: true,
    },
    ...TIMESTAMPS,
  ],
};

const ACTIVITY: CatalogEntity = {
  key: 'activity',
  label: 'Событие',
  labelPlural: 'История',
  model: 'activity',
  permission: 'crm:activities:read',
  // Журнал событий не правится и не удаляется — колонки `deletedAt` у него нет.
  softDelete: false,
  defaultSort: { field: 'occurredAt', direction: 'desc' },
  defaultColumns: ['kind', 'subject', 'actor', 'occurredAt'],
  fields: [
    // Видов события больше сорока, и список растёт с каждой фичей. Подписи к
    // ним живут в интерфейсе истории, здесь поле остаётся строкой: держать
    // вторую копию перечисления, которая молча разойдётся со схемой, хуже.
    { key: 'kind', label: 'Вид события', type: 'string', indexed: true },
    { key: 'subject', label: 'Тема', type: 'string' },
    { key: 'actor', label: 'Кто', type: 'string' },
    { key: 'occurredAt', label: 'Когда', type: 'date', indexed: true },
    {
      key: 'deal',
      label: 'Сделка',
      type: 'ref',
      ref: {
        entity: 'deal',
        labelPath: ['title'],
        foreignKey: 'dealId',
        inheritedVia: [
          { relation: 'document', entity: 'document', foreignKey: 'documentId', softDelete: true },
          { relation: 'task', entity: 'task', foreignKey: 'taskId', softDelete: true },
          { relation: 'event', entity: 'meeting', foreignKey: 'calendarEventId', softDelete: true },
        ],
      },
      indexed: true,
    },
    {
      key: 'contact',
      label: 'Контакт',
      type: 'ref',
      ref: {
        entity: 'contact',
        labelPath: ['firstName', 'lastName'],
        foreignKey: 'contactId',
        inheritedVia: [
          { relation: 'deal', entity: 'deal', foreignKey: 'dealId', softDelete: true },
          { relation: 'document', entity: 'document', foreignKey: 'documentId', softDelete: true },
          { relation: 'task', entity: 'task', foreignKey: 'taskId', softDelete: true },
          { relation: 'event', entity: 'meeting', foreignKey: 'calendarEventId', softDelete: true },
        ],
      },
      indexed: true,
    },
    {
      key: 'company',
      label: 'Компания',
      type: 'ref',
      ref: {
        entity: 'company',
        labelPath: ['name'],
        foreignKey: 'companyId',
        inheritedVia: [
          { relation: 'contact', entity: 'contact', foreignKey: 'contactId', softDelete: true },
          { relation: 'deal', entity: 'deal', foreignKey: 'dealId', softDelete: true },
          { relation: 'document', entity: 'document', foreignKey: 'documentId', softDelete: true },
          { relation: 'task', entity: 'task', foreignKey: 'taskId', softDelete: true },
          { relation: 'event', entity: 'meeting', foreignKey: 'calendarEventId', softDelete: true },
        ],
      },
      indexed: true,
    },
    {
      key: 'task',
      label: 'Задача',
      type: 'ref',
      ref: { entity: 'task', labelPath: ['title'], foreignKey: 'taskId' },
      indexed: true,
    },
    {
      key: 'document',
      label: 'Документ',
      type: 'ref',
      ref: { entity: 'document', labelPath: ['title'], foreignKey: 'documentId' },
      indexed: true,
    },
    { key: 'createdAt', label: 'Создано', type: 'date' },
  ],
};

const MEMORY: CatalogEntity = {
  key: 'memory',
  label: 'Запись памяти',
  labelPlural: 'Память',
  model: 'memoryRecord',
  permission: 'crm:memory:read',
  softDelete: true,
  route: '/memory',
  defaultSort: { field: 'updatedAt', direction: 'desc' },
  defaultColumns: ['level', 'key', 'valueText', 'importance', 'updatedAt'],
  fields: [
    { key: 'level', label: 'Уровень', type: 'enum', options: MEMORY_LEVEL, indexed: true },
    { key: 'key', label: 'Ключ', type: 'string' },
    { key: 'valueText', label: 'Текст', type: 'text', notSortable: true },
    { key: 'importance', label: 'Важность', type: 'number' },
    { key: 'pinned', label: 'Закреплена', type: 'boolean' },
    ...TIMESTAMPS,
  ],
  // `valueEmbedding` — колонка типа `vector(1536)`, Prisma её не умеет
  // выбирать. В каталоге её нет, поэтому исполнителю она недоступна в
  // принципе — это не забывчивость, а единственный способ её не запросить.
};

const ENTITY_LIST = [
  DEAL,
  DOCUMENT,
  COMPANY,
  CONTACT,
  TASK,
  NOTE,
  MEETING,
  ACTIVITY,
  MEMORY,
] as const;

export type EntityKey =
  | 'deal'
  | 'document'
  | 'company'
  | 'contact'
  | 'task'
  | 'note'
  | 'meeting'
  | 'activity'
  | 'memory';

/**
 * Колонка «Привязки» появляется у КАЖДОЙ сущности, чьи связи живут в отдельной
 * таблице (`ref.through`) — сегодня это заметка, завтра может быть что угодно.
 * Заводить её руками у каждой такой сущности значит однажды забыть.
 *
 * Сущностям с обычными колонками-ссылками (задача, документ, встреча) она не
 * нужна: у них «через что привязано» и так выражается отдельными колонками
 * «Сделка», «Контакт», «Компания».
 */
const LINKS_COLUMN_KEY = 'linkedTo';

function withLinksColumn(entity: CatalogEntity): CatalogEntity {
  const hasLinkTable = entity.fields.some((field) => field.ref?.through !== undefined);
  const fields: CatalogField[] = entity.fields.some((field) => field.key === 'id')
    ? [...entity.fields]
    : [
        {
          key: 'id',
          label: 'ID записи',
          type: 'string',
          indexed: true,
          filterOnly: true,
        },
        ...entity.fields,
      ];
  if (hasLinkTable && !fields.some((field) => field.key === LINKS_COLUMN_KEY)) {
    fields.push({
      key: LINKS_COLUMN_KEY,
      label: 'Привязки',
      type: 'links',
      // Фильтровать по ней нечего: для отбора есть сами поля привязок.
      notFilterable: true,
      notSortable: true,
    });
  }
  return {
    ...entity,
    fields,
  };
}

export const VIEW_CATALOG: Readonly<Record<EntityKey, CatalogEntity>> = Object.freeze(
  Object.fromEntries(ENTITY_LIST.map((entity) => [entity.key, withLinksColumn(entity)])) as Record<
    EntityKey,
    CatalogEntity
  >,
);

export const ENTITY_KEYS = ENTITY_LIST.map((entity) => entity.key) as readonly EntityKey[];

export function isEntityKey(value: string): value is EntityKey {
  return Object.prototype.hasOwnProperty.call(VIEW_CATALOG, value);
}

export function getEntity(key: EntityKey): CatalogEntity {
  return VIEW_CATALOG[key];
}

/**
 * Винительный падеж подписи — только для оборота «через …».
 *
 * Вывести его из `label` нельзя: род и склонение по правилам не берутся, а
 * «связь через сделка» читается как ошибка перевода. Таблица закрыта
 * перечислением `EntityKey`, поэтому новую сущность без подписи tsc не пропустит.
 */
const ACCUSATIVE: Record<EntityKey, string> = {
  deal: 'сделку',
  document: 'документ',
  company: 'компанию',
  contact: 'контакт',
  task: 'задачу',
  note: 'заметку',
  meeting: 'встречу',
  activity: 'событие',
  memory: 'запись памяти',
};

/**
 * Подпись пути наследования: «через сделку». Живёт в каталоге, потому что читателей
 * трое — ячейка представления, гриды задач и документов, карточки. Разойдясь, они
 * назовут один и тот же путь по-разному.
 */
export function inheritedViaLabel(entity: string): string {
  return isEntityKey(entity) ? `через ${ACCUSATIVE[entity]}` : 'через соседнюю запись';
}

export function findField(entity: EntityKey, fieldKey: string): CatalogField | undefined {
  return VIEW_CATALOG[entity].fields.find((field) => field.key === fieldKey);
}

/**
 * Поля, по которым можно фильтровать. Тяжёлые тексты остаются доступными —
 * поиск по вхождению это ровно то, зачем они нужны, — а отсеиваются те, что
 * помечены явно.
 */
export function filterableFields(entity: EntityKey): readonly CatalogField[] {
  return VIEW_CATALOG[entity].fields.filter((field) => field.notFilterable !== true);
}

export function sortableFields(entity: EntityKey): readonly CatalogField[] {
  return VIEW_CATALOG[entity].fields.filter(
    (field) =>
      field.notSortable !== true &&
      field.filterOnly !== true &&
      field.type !== 'ref' &&
      field.type !== 'tags',
  );
}

/** Поля, которые можно показать: колонкой таблицы, строкой карточки, разрезом. */
export function displayableFields(entity: EntityKey): readonly CatalogField[] {
  return VIEW_CATALOG[entity].fields.filter((field) => field.filterOnly !== true);
}
